import { createElement, useContext, useEffect } from 'react';
import {
  Navigate,
  Routes,
  Route,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import Home from 'containers/user/home/home';
import Reports from 'containers/user/reports/reports';
import Profile from 'containers/admin/profile/profile';
import Files from 'containers/user/files/files';
import ProfileForUserMode from 'containers/user/profile/profile';
import Learn from 'containers/user/learn/learn';
import ReportPage from 'containers/user/reports/report/report';
import FavoriteReports from 'containers/user/reports/favorite-reports/favorite-reports';

import GetStartedGuide from 'containers/user/learn/components/get-started-guide/get-started-guide';

import Articles from 'containers/user/learn/containers/content/articles/articles';
import Article from 'containers/user/learn/containers/content/articles/article/article';
import Downloads from 'containers/user/learn/containers/content/downloads/downloads';
import Download from 'containers/user/learn/containers/content/downloads/download/download';
import Guides from 'containers/user/learn/containers/content/guides/guides';
import Guide from 'containers/user/learn/containers/content/guides/guide/guide';
import Webinars from 'containers/user/learn/containers/content/webinars/webinars';
import Webinar from 'containers/user/learn/containers/content/webinars/webinar/webinar';
import ProductAnnouncements from 'containers/user/learn/containers/content/product-announcements/product-announcements';
import ProductAnnouncement from 'containers/user/learn/containers/content/product-announcements/product-announcement/product-announcement';
import Courses from 'containers/user/learn/containers/content/cources/courses';
import CourseOverview from 'containers/user/learn/containers/content/cources/cource-overview/course-overview';

import ClientsAdminPage from 'containers/admin/clients/clients-admin-page';
import ClientDetails from 'containers/admin/clients/client/client-details';
import ClientProfile from 'containers/admin/clients/client/client-profile/client-profile';

import UsersAdminPage from 'containers/admin/users/users-admin-page';
import UserPage from 'containers/admin/users/user/user-page/user-page';
import UserProfile from 'containers/admin/users/user/user-profile/user-profile';

import ReportsAdminPage from 'containers/admin/reports/reports-admin.page';
import AdminReport from 'containers/admin/reports/report/report';

import ProductsAdminPage from 'containers/admin/products/products-admin-page';
import AdminProduct from 'containers/admin/products/product/product';

import FilePage from 'containers/admin/clients/client/tabs-components/files-tab/file/file-page/file-page';
import File from 'containers/admin/clients/client/tabs-components/files-tab/file/file';
import CourseDetails from 'containers/user/learn/containers/content/cources/course-details/course-details';

import GoalsManager from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-manager';

import Product from 'containers/admin/clients/client/tabs-components/products-tab/product/product';
import CriticalElementManager from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element-manager';
import AidStudioDetails from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details';
import AidStudio from 'containers/user/aid-studio/aid-studio';
import AidStudioDetailsWrapper from 'containers/user/aid-studio/aid-studio-details-wrapper';

// import MyGoals from 'containers/user/goals/goals';
import GoalsMonitoring from 'containers/user/goals/goals-monitoring/goals-monitoring';
import AdminGoalsMonitoring from 'containers/admin/clients/client/tabs-components/goals-tab/goals-monitoring/admin-goals-monitoring';
import CriticalElementsMonitoring from 'containers/user/critical-elements-monitoring/critical-elements-monitoring';

import Insights from 'containers/user/insights/insights';
import InsightDetail from 'containers/user/insights/insight-detail/insight-detail';

import ClientReportView from 'containers/admin/clients/client/tabs-components/reports-tab/client-report-view';
import InsightDetails from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/insight-details';
import AiAssistantPage from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-assistant-page';

import { useImpersonation } from 'hooks/use-impersonation';
import { FeatureFlagsContext } from 'components/shared/feature-flags';

// TODO(ts-migration): JS components that use useParams/useContext internally; props appear required to TS but are optional at runtime
const UntypedFile = File as React.ComponentType;
const UntypedClientProfile = ClientProfile as React.ComponentType<{
  pageName?: string;
}>;
const UntypedReportsAdminPage = ReportsAdminPage as React.ComponentType;
const UntypedProductsAdminPage = ProductsAdminPage as React.ComponentType;

type RouteComponent = React.ElementType;

// TODO(ts-migration): these HOCs use generic component types that need full typing in a follow-up
const withParams =
  (Component: RouteComponent, paramMapping: Record<string, string>) =>
  (props: Record<string, unknown>) => {
    const params = useParams();
    const paramProps: Record<string, string | undefined> = {};

    Object.entries(paramMapping).forEach(([propName, paramName]) => {
      paramProps[propName] = params[paramName || propName];
    });

    return createElement(Component, { ...paramProps, ...props });
  };

const withLocation =
  (Component: RouteComponent) => (props: Record<string, unknown>) => {
    const location = useLocation();
    const state = location?.state as { notificationParams?: unknown } | null;

    return createElement(Component, {
      notificationParams: state?.notificationParams,
      ...props,
    });
  };

const withLocationAndParams =
  (Component: RouteComponent, paramMapping: Record<string, string>) =>
  (props: Record<string, unknown>) => {
    const params = useParams();
    const location = useLocation();
    const state = location?.state as { notificationParams?: unknown } | null;
    const paramProps: Record<string, string | undefined> = {};

    Object.entries(paramMapping).forEach(([propName, paramName]) => {
      paramProps[propName] = params[paramName || propName];
    });

    return createElement(Component, {
      notificationParams: state?.notificationParams,
      ...paramProps,
      ...props,
    });
  };

const EnhancedComponents = {
  ReportPage: withParams(ReportPage, { reportId: 'id' }),
  Article: withParams(Article, { articleId: 'id' }),
  Guide: withParams(Guide, { guideId: 'id' }),
  Download: withParams(Download, { downloadId: 'id' }),
  Webinar: withParams(Webinar, { webinarId: 'id' }),
  ProductAnnouncement: withParams(ProductAnnouncement, { releaseNoteId: 'id' }),
  CourseOverview: withParams(CourseOverview, { courseId: 'id' }),
  AdminReport: withParams(AdminReport, { reportId: 'id' }),
  AdminProduct: withParams(AdminProduct, { productId: 'id' }),
  Product: withParams(Product, { productId: 'id', clientId: 'clientId' }),
  GoalsMonitoring: withParams(GoalsMonitoring, { goalId: 'id' }),
  AdminGoalsMonitoring: withParams(AdminGoalsMonitoring, {
    clientId: 'clientId',
    year: 'year',
    goalType: 'goalType',
    goalCategory: 'goalCategory',
    goalId: 'goalId',
  }),
  InsightDetail: withParams(InsightDetail, { insightId: 'id' }),
  ClientReportView: withParams(ClientReportView, {
    id: 'id',
    clientId: 'clientId',
  }),
  InsightDetails: withParams(InsightDetails, {
    clientId: 'clientId',
    insightId: 'insightId',
  }),

  UsersAdminPage: withLocation(UsersAdminPage),
  UserProfile: withLocation(UserProfile),

  UserPage: withLocationAndParams(UserPage, { clientId: 'clientId' }),
  UserPageProfile: withLocation(UserPage),
  FilePage: withParams(FilePage, {
    clientId: 'clientId',
    fileId: 'fileId',
  }),
};

const ClientRedirect = () => {
  const { id } = useParams();

  return <Navigate replace to={`/admin/clients/${id}/users`} />;
};

const UserRedirect = () => {
  const { id } = useParams();

  return <Navigate replace to={`/admin/users/${id}/profile`} />;
};

interface MainAuthenticatedProps {
  user: {
    data?: {
      internalAdmin?: boolean;
      fdUser?: boolean;
      clientAdmin?: boolean;
      fdAnalyst?: boolean;
    };
  } | null;
}

const MainAuthenticated = ({ user }: MainAuthenticatedProps) => {
  const internalAdmin = user?.data?.internalAdmin;
  const isAdmin = user?.data && (user?.data?.fdUser || user?.data?.clientAdmin);
  const mvAnalyst = user?.data?.fdAnalyst;
  const { featureFlags, featureFlagsLoaded } = useContext(FeatureFlagsContext);
  const { impersonatingUser, isImpersonating } = useImpersonation();
  const navigate = useNavigate();

  const isUserView = impersonatingUser || isImpersonating();
  const showUserRoutes = !isAdmin || isUserView;

  useEffect(() => {
    if (window.location.pathname === '/') {
      if (isAdmin && !isUserView) {
        navigate('/admin/clients', { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    }
  }, [isAdmin, isUserView, navigate]);

  const userRoutes = showUserRoutes
    ? [
        { path: 'home', element: <Home /> },
        { path: 'reports/:id', element: <EnhancedComponents.ReportPage /> },
        { path: 'reports/:reportType?', element: <Reports /> },
        { path: 'reports/all', element: <Reports /> },
        { path: 'reports/favorites', element: <FavoriteReports /> },
        { path: 'profile', element: <ProfileForUserMode /> },
        { path: 'learn', element: <Learn /> },
        { path: 'learn/get-started-guide', element: <GetStartedGuide /> },
        { path: 'learn/content/articles', element: <Articles /> },
        {
          path: 'learn/content/articles/:id',
          element: <EnhancedComponents.Article />,
        },
        { path: 'learn/content/guides', element: <Guides /> },
        {
          path: 'learn/content/guides/:id',
          element: <EnhancedComponents.Guide />,
        },
        { path: 'learn/content/downloads', element: <Downloads /> },
        {
          path: 'learn/content/downloads/:id',
          element: <EnhancedComponents.Download />,
        },
        { path: 'learn/content/webinars', element: <Webinars /> },
        {
          path: 'learn/content/webinars/:id',
          element: <EnhancedComponents.Webinar />,
        },
        {
          path: 'learn/content/product-announcements',
          element: <ProductAnnouncements />,
        },
        {
          path: 'learn/content/product-announcements/:id',
          element: <EnhancedComponents.ProductAnnouncement />,
        },
        { path: 'learn/content/glossary', element: <div>Glossary</div> },

        { path: 'learn/courses', element: <Courses /> },
        {
          path: 'learn/course/:id',
          element: <EnhancedComponents.CourseOverview />,
        },
        {
          path: 'learn/courses/:courseId/lesson/:lessonId',
          element: <CourseDetails />,
        },
        { path: 'files', element: <Files /> },
        featureFlags['tab-fao'] && {
          path: 'aid-studio',
          element: <AidStudio />,
        },
        featureFlags['tab-fao'] && {
          path: 'aid-studio/:modelId/:step?',
          element: <AidStudioDetailsWrapper />,
        },
        // TODO: delete the user goal monitoring page (this route and
        // containers/user/goals/goals-monitoring). Hidden together with the standalone Goals page.
        // featureFlags['goals-monitoring'] && {
        //   path: 'goals/:year/:goalId/monitoring',
        //   element: <GoalsMonitoring />,
        // },
        featureFlags['critical-elements-monitoring'] && {
          path: 'monitoring',
          element: <CriticalElementsMonitoring />,
        },
        featureFlags['ai-assistant'] && {
          path: 'ai-assistant',
          element: <AiAssistantPage />,
        },
      ]
    : [];

  // TODO: delete the standalone user Goals page (this route, its commented-out import and
  // `...goalsRoutes` spread, and containers/user/goals/goals.tsx). Goals now live on the Goals
  // tab of the Monitoring page.
  // const goalsRoutes = showUserRoutes
  //   ? [{ path: 'goals/:year?', element: <MyGoals /> }]
  //   : [];

  // User insights routes
  const insightsRoutes = showUserRoutes
    ? [
        { path: 'insights', element: <Insights /> },
        {
          path: 'insights/:id',
          element: <EnhancedComponents.InsightDetail />,
        },
      ]
    : [];

  // Analyst only routes
  const adminFileRoutes = !mvAnalyst
    ? [{ path: 'admin/clients/:clientId/new-file', element: <UntypedFile /> }]
    : [];

  // Admin routes
  const commonAdminRoutes = isAdmin
    ? [
        { path: 'admin/profile', element: <Profile /> },
        {
          path: 'admin/clients/:clientId/products/:id',
          element: <EnhancedComponents.Product />,
        },
        {
          path: 'admin/clients/:clientId/reports/:id',
          element: <EnhancedComponents.ClientReportView />,
        },
        {
          path: 'admin/clients/:clientId/insights/:insightId',
          element: <EnhancedComponents.InsightDetails />,
        },
        {
          path: 'admin/clients/:clientId/critical-elements/:year/:id',
          element: <CriticalElementManager />,
        },
        {
          path: 'admin/clients',
          element: <ClientsAdminPage />,
        },
        {
          path: 'admin/clients/new',
          element: <UntypedClientProfile pageName="Add new client" />,
        },
        {
          path: 'admin/clients/:clientId/users/:id',
          element: <EnhancedComponents.UserPage />,
        },
        {
          path: 'admin/clients/:clientId/files/:fileId',
          element: <EnhancedComponents.FilePage />,
        },
        {
          path: 'admin/clients/:clientId/goals/:year/:goalType/:goalCategory/:goalId?',
          element: <GoalsManager />,
        },
        {
          path: 'admin/clients/:clientId/goals/:year/:goalType/:goalCategory/:goalId/monitoring',
          element: <EnhancedComponents.AdminGoalsMonitoring />,
        },
        {
          path: 'admin/clients/:clientId/users/:id/profile',
          element: <EnhancedComponents.UserPageProfile />,
        },
        {
          path: 'admin/clients/:clientId/aid-studio/new',
          element: <AidStudioDetails />,
        },
        {
          path: 'admin/clients/:clientId/aid-studio/:modelId/:step?',
          element: <AidStudioDetails />,
        },
        featureFlags['ai-assistant'] && {
          path: 'admin/clients/:clientId/ai-assistant',
          element: <AiAssistantPage />,
        },
        { path: 'admin/clients/:id', element: <ClientRedirect /> },
        { path: 'admin/clients/:id/*', element: <ClientDetails /> },
        { path: 'admin/users', element: <EnhancedComponents.UsersAdminPage /> },
        {
          path: 'admin/users/new',
          element: <EnhancedComponents.UserProfile />,
        },
        { path: 'admin/users/:id', element: <UserRedirect /> },
        {
          path: 'admin/users/:id/profile',
          element: <EnhancedComponents.UserPageProfile />,
        },
      ]
    : [];

  // Super admin only routes
  const nonInternalAdminRoutes =
    isAdmin && !internalAdmin
      ? [
          { path: 'admin/reports', element: <UntypedReportsAdminPage /> },
          {
            path: 'admin/reports/:id',
            element: <EnhancedComponents.AdminReport />,
          },
          { path: 'admin/products', element: <UntypedProductsAdminPage /> },
          {
            path: 'admin/products/:id',
            element: <EnhancedComponents.AdminProduct />,
          },
        ]
      : [];

  const routes = [
    featureFlagsLoaded && {
      path: '*',
      element: <Navigate replace to={isAdmin ? '/admin/clients' : '/home'} />,
    },
    ...userRoutes,
    // ...goalsRoutes,
    ...insightsRoutes,
    ...adminFileRoutes,
    ...commonAdminRoutes,
    ...nonInternalAdminRoutes,
  ];

  const visibleRoutes = routes.filter(
    (route): route is { path: string; element: React.ReactElement } =>
      Boolean(route)
  );

  return (
    <main
      id="main-content"
      className="flex flex-col flex-1 items-center w-full"
    >
      <Routes>
        {visibleRoutes.map((route, index) => (
          <Route
            key={`route-${index}`}
            path={route.path}
            element={route.element}
          />
        ))}
      </Routes>
    </main>
  );
};

export default MainAuthenticated;
